'use client';

import { useState } from 'react';
import Link from 'next/link';

export default function TermsOfUsePage() {
  const [accepted, setAccepted] = useState(false);

  const sections = [
    {
      id: 'platform-purpose',
      title: '1. Platform Purpose and Scope',
      content: `ChronoMind™ AI Governance Platform ("the Platform") provides documentation tools, risk assessment frameworks, and compliance management capabilities for organizations implementing artificial intelligence systems. The Platform is designed to assist with AI governance workflows, regulatory compliance tracking, and risk management documentation.

IMPORTANT: This Platform provides tools and frameworks only. It does not constitute legal advice, regulatory certification, or compliance guarantee.`,
    },
    {
      id: 'user-responsibilities',
      title: '2. User Responsibilities',
      content: `Users of the Platform acknowledge and agree that:

• You are solely responsible for ensuring your AI systems comply with all applicable laws, regulations, and industry standards
• The Platform's risk assessments, control recommendations, and compliance mappings are guidance tools only
• You must consult qualified legal counsel and compliance professionals for specific regulatory requirements
• You are responsible for the accuracy and completeness of all data entered into the Platform
• You must conduct independent verification of all AI system risks and compliance requirements
• You are responsible for implementing appropriate controls and safeguards for your AI systems`,
    },
    {
      id: 'no-legal-advice',title: '3. No Legal Advice or Regulatory Certification',
      content: `CRITICAL DISCLAIMER:

The Platform does NOT provide:
• Legal advice or legal opinions
• Regulatory certification or compliance attestation
• Guarantee of regulatory compliance
• Audit or assessment services
• Professional consulting services

The Platform's framework mappings (NIST AI RMF, ISO/IEC 42001, EU AI Act, etc.) are informational references only. Compliance with these frameworks requires independent assessment by qualified professionals.

Users must engage qualified legal counsel, compliance advisors, and certified auditors for:
• Regulatory compliance verification
• Legal interpretation of AI regulations
• Certification and audit preparation
• Jurisdiction-specific compliance requirements`,
    },
    {
      id: 'data-handling',
      title: '4. Data Handling and Privacy',
      content: `The Platform processes and stores:
• Organization information and user profiles
• AI use case descriptions and risk assessments
• Control implementation records and evidence files
• Audit logs and compliance documentation

Data Security:
• All data is encrypted in transit and at rest
• Access is controlled through role-based permissions
• Multi-tenant isolation ensures organizational data separation
• Regular security assessments and monitoring

Data Ownership:
• You retain all rights to your data
• You may export your data at any time
• You may request data deletion upon account termination

Data Privacy:
• We do not sell or share your data with third parties
• We process data only as necessary to provide Platform services
• See our Privacy Policy for detailed information`,
    },
    {
      id: 'liability-limitations',
      title: '5. Limitation of Liability',
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

• The Platform is provided "AS IS" without warranties of any kind
• We do not warrant that the Platform will meet your specific compliance requirements
• We are not liable for any regulatory fines, penalties, or enforcement actions
• We are not liable for any damages arising from your use of the Platform
• We are not liable for any decisions made based on Platform recommendations
• We are not liable for any data loss, system failures, or service interruptions

Your use of the Platform is at your own risk. You acknowledge that:
• AI governance and compliance are complex, evolving fields
• Regulatory requirements vary by jurisdiction and change over time
• The Platform cannot replace professional judgment and expertise
• You must independently verify all compliance requirements`,
    },
    {
      id: 'intellectual-property',
      title: '6. Intellectual Property Rights',
      content: `Platform Ownership:
• The Platform, including all software, frameworks, and documentation, is owned by ChronoMind™
• You receive a limited, non-exclusive license to use the Platform
• You may not copy, modify, or distribute Platform software or content

Your Content:
• You retain ownership of all data and content you upload
• You grant us a license to process your content to provide Platform services
• You represent that you have rights to all content you upload

Framework References:
• NIST AI RMF, ISO/IEC standards, and other frameworks are referenced for informational purposes
• These frameworks are owned by their respective organizations
• Users must obtain official framework documentation from authoritative sources`,
    },
    {
      id: 'service-availability',
      title: '7. Service Availability and Modifications',
      content: `Service Availability:
• We strive for 99.9% uptime but do not guarantee uninterrupted service
• Scheduled maintenance will be announced in advance when possible
• We are not liable for service interruptions or data access delays

Platform Modifications:
• We may modify, update, or discontinue Platform features at any time
• We will provide reasonable notice of material changes
• Continued use after changes constitutes acceptance of modifications

Support:
• Support is provided based on your subscription tier
• We will make reasonable efforts to respond to support requests
• We do not guarantee specific response times or issue resolution`,
    },
    {
      id: 'termination',
      title: '8. Account Termination',
      content: `You may terminate your account at any time by:
• Contacting support to request account closure
• Exporting your data before termination
• Settling any outstanding payment obligations

We may terminate or suspend your account if:
• You violate these Terms of Use
• You engage in fraudulent or illegal activities
• Your account poses security risks to the Platform
• You fail to pay subscription fees

Upon termination:
• Your access to the Platform will be revoked
• Your data will be retained for 30 days for recovery
• After 30 days, your data will be permanently deleted
• You remain responsible for any obligations incurred before termination`,
    },
    {
      id: 'governing-law',
      title: '9. Governing Law and Dispute Resolution',
      content: `These Terms are governed by the laws of [Jurisdiction] without regard to conflict of law principles.

Dispute Resolution:
• Disputes will be resolved through good faith negotiation
• If negotiation fails, disputes will be submitted to binding arbitration
• Arbitration will be conducted under [Arbitration Rules]
• Each party bears its own costs unless otherwise awarded

Class Action Waiver:
• You agree to resolve disputes on an individual basis only
• You waive any right to participate in class action lawsuits
• You waive any right to jury trial`,
    },
    {
      id: 'changes-to-terms',
      title: '10. Changes to Terms',
      content: `We may update these Terms of Use from time to time. Changes will be effective:
• Immediately upon posting for minor clarifications
• 30 days after notice for material changes

We will notify you of material changes by:
• Email notification to your registered address
• In-app notification upon login
• Posting notice on the Platform

Your continued use after changes constitutes acceptance. If you do not agree to changes, you must discontinue use and terminate your account.`,
    },
    {
      id: 'contact',
      title: '11. Contact Information',
      content: `For questions about these Terms of Use, please contact:

ChronoMind™ AI Governance Platform
Email: legal@chronomind.ai
Support: support@chronomind.ai

For compliance and regulatory questions, please consult qualified legal counsel in your jurisdiction.`,
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <Link href="/" className="flex items-center gap-2">
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="40" height="40" rx="8" fill="var(--color-primary)" />
              <path
                d="M20 10L12 16V28H16V22H24V28H28V16L20 10Z"
                fill="var(--color-primary-foreground)"
                opacity="0.9"
              />
              <circle cx="20" cy="18" r="2" fill="var(--color-accent)" />
            </svg>
            <span className="font-heading text-xl font-bold text-foreground">ChronoMind™</span>
          </Link>
          <Link
            href="/login"
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Back to Login
          </Link>
        </div>
      </header>
      {/* Main Content */}
      <main className="flex-1 px-6 py-12">
        <div className="mx-auto max-w-4xl">
          {/* Title */}
          <div className="mb-8 text-center">
            <h1 className="font-heading text-4xl font-bold text-foreground">Terms of Use</h1>
            <p className="mt-2 text-muted-foreground">Last Updated: February 24, 2026</p>
          </div>

          {/* Critical Disclaimer Box */}
          <div className="mb-8 rounded-lg border-2 border-destructive bg-destructive/5 p-6">
            <div className="flex items-start gap-4">
              <svg
                className="mt-1 h-6 w-6 flex-shrink-0 text-destructive"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <h2 className="mb-2 text-lg font-bold text-destructive">IMPORTANT LEGAL NOTICE</h2>
                <p className="text-sm text-foreground">
                  This Platform provides <strong>documentation tools and risk assessment frameworks only</strong>. It does NOT provide legal advice, regulatory certification, or compliance guarantee. Users remain solely responsible for ensuring their AI systems comply with all applicable laws and regulations. You MUST consult qualified legal counsel and compliance professionals for specific regulatory requirements.
                </p>
              </div>
            </div>
          </div>

          {/* Table of Contents */}
          <div className="mb-8 rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-xl font-bold text-foreground">Table of Contents</h2>
            <nav className="space-y-2">
              {sections?.map((section) => (
                <a
                  key={section?.id}
                  href={`#${section?.id}`}
                  className="block text-sm text-primary hover:underline"
                >
                  {section?.title}
                </a>
              ))}
            </nav>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {sections?.map((section) => (
              <section
                key={section?.id}
                id={section?.id}
                className="scroll-mt-8 rounded-lg border border-border bg-card p-6"
              >
                <h2 className="mb-4 text-2xl font-bold text-foreground">{section?.title}</h2>
                <div className="whitespace-pre-line text-muted-foreground">{section?.content}</div>
              </section>
            ))}
          </div>

          {/* Acceptance Section */}
          <div className="mt-12 rounded-lg border border-border bg-card p-6">
            <h2 className="mb-4 text-xl font-bold text-foreground">Acceptance of Terms</h2>
            <p className="mb-4 text-muted-foreground">
              By using the ChronoMind™ AI Governance Platform, you acknowledge that you have read,
              understood, and agree to be bound by these Terms of Use. If you do not agree to these
              terms, you must not use the Platform.
            </p>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="accept-terms"
                checked={accepted}
                onChange={(e) => setAccepted(e?.target?.checked)}
                className="mt-1 h-4 w-4 rounded border-border text-primary focus:ring-2 focus:ring-primary/20"
              />
              <label htmlFor="accept-terms" className="text-sm text-foreground">
                I have read and agree to the Terms of Use. I understand that this Platform provides
                documentation tools only and does not constitute legal advice or regulatory
                certification.
              </label>
            </div>
            {accepted && (
              <div className="mt-4">
                <Link
                  href="/login"
                  className="inline-block rounded-md bg-primary px-6 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Proceed to Platform
                </Link>
              </div>
            )}
          </div>

          {/* Version History */}
          <div className="mt-8 rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-2 text-sm font-semibold text-foreground">Version History</h3>
            <ul className="space-y-1 text-xs text-muted-foreground">
              <li>• Version 1.0 - February 24, 2026: Initial Terms of Use</li>
            </ul>
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="border-t border-border bg-card px-6 py-6">
        <div className="mx-auto max-w-7xl text-center text-sm text-muted-foreground">
          <p>
            © 2026 ChronoMind™ AI Governance Platform. All rights reserved. |{' '}
            <Link href="/terms-of-use" className="text-primary hover:underline">
              Terms of Use
            </Link>{' '}
            |{' '}
            <a href="mailto:legal@chronomind.ai" className="text-primary hover:underline">
              Contact Legal
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}